import { CONTRACT_SLUG_PREFIX } from '../../../constants';
import { CommunityContract } from '../../../types/contracts';
import { convertAreaByUnitSystem, convertAreaToBaseUnitSystem, type UnitSource } from '../../../utils/helpers/units';

export const getApartmentTypeGroupSlug = (id: number) => `ATG-${id}`;
export const getApartmentTypeSlug = (id: number) => `AT-${id}`;
export const getContractsSlug = (contracts: CommunityContract[]) =>
  contracts
    .sort((a: CommunityContract, b: CommunityContract) => a.type - b.type)
    .map((item: CommunityContract) => `${CONTRACT_SLUG_PREFIX}-${item.type}-${item.refund_percentage}`)
    .join('|');

const getAccommodationSortValue = (value: unknown) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  return Number.MAX_SAFE_INTEGER;
};

export const sortAccommodationEntriesByBaseRent = <
  T extends { base_rent?: number; sq_footage?: number; apartment_type_id?: number },
>(
  entries: [string, T][]
) =>
  [...entries].sort(([firstKey, firstEntry], [secondKey, secondEntry]) => {
    const baseRentDiff =
      getAccommodationSortValue(firstEntry?.base_rent) - getAccommodationSortValue(secondEntry?.base_rent);

    if (baseRentDiff !== 0) {
      return baseRentDiff;
    }

    const sqFootageDiff =
      getAccommodationSortValue(firstEntry?.sq_footage) - getAccommodationSortValue(secondEntry?.sq_footage);

    if (sqFootageDiff !== 0) {
      return sqFootageDiff;
    }

    if (typeof firstEntry?.apartment_type_id === 'number' && typeof secondEntry?.apartment_type_id === 'number') {
      return firstEntry.apartment_type_id - secondEntry.apartment_type_id;
    }

    return firstKey.localeCompare(secondKey);
  });

export const sortAccommodationGroupByBaseRent = <
  T extends { base_rent?: number; sq_footage?: number; apartment_type_id?: number },
>(
  group: Record<string, T> | undefined
) => Object.fromEntries(sortAccommodationEntriesByBaseRent(Object.entries(group ?? {})));

const convertAccommodationAreaValues = <T>(data: T, convertArea: (value: number) => number): T => {
  if (Array.isArray(data)) {
    return data.map((item) => convertAccommodationAreaValues(item, convertArea)) as T;
  }

  if (!data || typeof data !== 'object') {
    return data;
  }

  const convertedData = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, convertAccommodationAreaValues(value, convertArea)])
  );

  const squareFootage = (data as { sq_footage?: unknown }).sq_footage;

  if (typeof squareFootage === 'number') {
    return {
      ...convertedData,
      sq_footage: convertArea(squareFootage),
    } as T;
  }

  return convertedData as T;
};

export const convertAccommodationAreasByUnitSystem = <T>(data: T, unitSource?: UnitSource): T =>
  convertAccommodationAreaValues(data, (value) => convertAreaByUnitSystem(value, unitSource));

export const convertAccommodationAreasToBaseUnitSystem = <T>(data: T, unitSource?: UnitSource): T =>
  convertAccommodationAreaValues(data, (value) => convertAreaToBaseUnitSystem(value, unitSource));
